import clsx from "clsx";
import React from "react";
import { Button } from "web/react/emo/button";
import { Heading } from "web/react/emo/heading";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack/v-stack";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { gettext } from "web/script/modules/django-i18n";
import * as styles from "./editorial-details.css";

export type EditorialDetailObject = {
    isSponsored: boolean;
    title: string;
    caption: string;
};
interface EditorialDetailsProps {
    variant?: "primary" | "secondary" | "compact";
    details: EditorialDetailObject;
}

/**
 * Design System
 * @name EditorialDetails
 * @version 1.0
 * @design https://www.figma.com/file/EJXteyeEMjMgKETDQiZDli/Design-Sprint%3A-FY24-Home?type=design&node-id=3767-78162&mode=design&t=Otf1m3aYXI7YYf4b-4
 */
export function EditorialDetails({
    variant = "primary",
    details,
}: EditorialDetailsProps): React.ReactElement {
    const { isSponsored, title, caption } = details;
    const isPrimary = variant === "primary";
    const isSecondary = variant === "secondary";
    const isCompact = variant === "compact";
    const { isMobileViewport, isDesktopViewport } = useDomViewport();
    return (
        <section className={clsx({ [styles.wrapper]: !isCompact })}>
            <div
                className={clsx(styles.content, {
                    [styles.secondaryDesktopContent]: isSecondary && isDesktopViewport,
                    [styles.primaryContent]: isPrimary,
                })}
            >
                <VStack spacing={isPrimary ? "lg" : "md"}>
                    <VStack spacing={"xs"}>
                        {isSponsored && (
                            <Text textStyle={"caption-3"} className={styles.sponsored}>
                                {gettext("general.sponsored_label")}
                            </Text>
                        )}

                        <Heading
                            textStyle={
                                isMobileViewport && !isPrimary
                                    ? "large-title-2"
                                    : isCompact
                                    ? "title-2-v2"
                                    : "large-title-v2"
                            }
                        >
                            {title}
                        </Heading>
                    </VStack>
                    <Text textStyle={isCompact ? "body-3" : "body-2-v2"} className={styles.body}>
                        {caption}
                    </Text>
                </VStack>
                <div className={clsx({ [styles.buttonWrapper]: isPrimary })}>
                    <Button
                        title={
                            isPrimary
                                ? gettext("general.explore_now")
                                : gettext("general.read_more")
                        }
                        width={isPrimary ? "full" : "fit-content"}
                        variant={isPrimary ? "primary" : "secondary"}
                    />
                </div>
            </div>
        </section>
    );
}
